import {
  ApiError,
  apiPost,
  el,
  getConfig,
  type GoogleIdApi,
  type SeopyoConfig,
} from "./shared.js";
import { errorText, resolveStrings, type Catalogue } from "./i18n.js";

// Mounts into every <div data-subscribe> on the page.
//
// Signed out, this widget is one button and nothing else: subscribing by email
// is documented by the page around it, in the blog's own words. There is no
// box to type an address into, which is what lets it offer unsubscribing at
// all, and signing in only reads — the buttons in the panel are what write.
//
// The credential is held in a module variable and nowhere else. The moderation
// page's sessionStorage cache was argued from that page's own guarantees
// (strict CSP, no innerHTML, no third-party script but Google's), and a blog
// page offers none of them.

const GSI_SRC = "https://accounts.google.com/gsi/client";

let gsiPromise: Promise<GoogleIdApi> | null = null;

/**
 * On demand, as with Turnstile: a reader who never subscribes should not pay
 * for a third-party script. The poll after onload is because the script defines
 * window.google as it runs, and the load event has been seen to win that race.
 */
function loadGoogleIdentity(): Promise<GoogleIdApi> {
  gsiPromise ??= new Promise<GoogleIdApi>((resolve, reject) => {
    const ready = () => window.google?.accounts?.id;
    const existing = ready();
    if (existing) return resolve(existing);

    const script = el("script", { src: GSI_SRC, async: true, defer: true });
    script.onload = () => {
      const deadline = Date.now() + 5000;
      const poll = () => {
        const api = ready();
        if (api) return resolve(api);
        if (Date.now() > deadline)
          return reject(new Error("Google Sign-In is unavailable."));
        setTimeout(poll, 50);
      };
      poll();
    };
    script.onerror = () => reject(new Error("Google Sign-In failed to load."));
    document.head.appendChild(script);
  });
  return gsiPromise;
}

interface Subscription {
  email: string;
  subscribed: boolean;
}

class SubscribeWidget {
  /** The signed-out state: the button, and nothing else. */
  private readonly row = el("div", { class: "seopyo-subscribe-row" });
  private readonly googleSlot = el("div", { class: "seopyo-google" });

  /** The signed-in state, which replaces the row entirely. */
  private readonly panel = el("div", { class: "seopyo-panel", hidden: true });
  private readonly state: HTMLElement;
  private readonly address = el("p", { class: "seopyo-who" });
  private readonly subscribeButton: HTMLButtonElement;
  private readonly unsubscribeButton: HTMLButtonElement;

  private readonly message = el("p", { class: "seopyo-status" });

  /** Null until a status lookup has answered. */
  private current: Subscription | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly config: SeopyoConfig,
    private readonly text: Catalogue,
  ) {
    const { subscribe } = text;

    this.state = el("p", { class: "seopyo-flash", role: "status" });

    this.subscribeButton = el(
      "button",
      { type: "button", class: "seopyo-choice" },
      subscribe.subscribe,
    );
    this.unsubscribeButton = el(
      "button",
      { type: "button", class: "seopyo-choice" },
      subscribe.unsubscribe,
    );

    this.subscribeButton.addEventListener("click", () => void this.set(true));
    this.unsubscribeButton.addEventListener(
      "click",
      () => void this.set(false),
    );
  }

  init(): void {
    this.root.classList.add("seopyo", "seopyo-subscribe");
    // So a Korean box inside an English page is read as Korean.
    this.root.lang = this.text.lang;

    const google = Boolean(this.config.google_client_id);
    this.row.replaceChildren(...(google ? [this.googleSlot] : []));

    this.panel.replaceChildren(
      this.state,
      this.address,
      el(
        "div",
        { class: "seopyo-choices", role: "group" },
        this.subscribeButton,
        this.unsubscribeButton,
      ),
    );

    this.root.replaceChildren(this.row, this.panel, this.message);

    // Without a client ID there is no button, and an empty gap looks broken
    // rather than unconfigured.
    if (!google) this.note(this.text.subscribe.unconfigured, true);
  }

  async mountGoogleButton(clientId: string): Promise<void> {
    const google = await loadGoogleIdentity();
    initializeOnce(google, clientId);
    google.renderButton(this.googleSlot, {
      theme: "outline",
      size: "large",
      text: "continue_with",
      // The button renders its own label in its own document; without this it
      // follows the reader's Google account rather than the page.
      locale: this.text.lang,
    });
  }

  private note(text: string, isError = false): void {
    this.message.className = isError ? "seopyo-error" : "seopyo-status";
    this.message.textContent = text;
  }

  /** Both buttons stay put and stay enabled; only the shading moves. */
  private paint(): void {
    if (!this.current) return;
    this.state.textContent = this.current.subscribed
      ? this.text.subscribe.subscribedState
      : this.text.subscribe.unsubscribedState;
    this.address.textContent = this.current.email;
    this.subscribeButton.setAttribute(
      "aria-pressed",
      String(this.current.subscribed),
    );
    this.unsubscribeButton.setAttribute(
      "aria-pressed",
      String(!this.current.subscribed),
    );
  }

  private busy(text: string): void {
    for (const button of [this.subscribeButton, this.unsubscribeButton])
      button.setAttribute("disabled", "");
    this.note(text);
  }

  private idle(): void {
    for (const button of [this.subscribeButton, this.unsubscribeButton])
      button.removeAttribute("disabled");
  }

  checking(): void {
    this.note(this.text.subscribe.checking);
  }

  show(subscription: Subscription): void {
    this.current = subscription;
    this.row.hidden = true;
    this.panel.hidden = false;
    this.idle();
    this.note("");
    this.paint();
  }

  fail(error: unknown): void {
    this.idle();
    // A rejected credential leaves nothing to manage, so the reader goes back
    // to the button rather than to a dead panel.
    if (error instanceof ApiError && error.code === "google_invalid") {
      this.current = null;
      this.panel.hidden = true;
      this.row.hidden = false;
    }
    this.note(
      error instanceof ApiError
        ? errorText(this.text, error.code, error.message)
        : this.text.subscribe.failed,
      true,
    );
  }

  private async set(subscribed: boolean): Promise<void> {
    // The shaded button is there to show where they stand, not to be pressed.
    if (!this.current || this.current.subscribed === subscribed) return;

    this.busy(this.text.subscribe.saving);
    try {
      this.current = await apiPost<Subscription>("/subscribe", {
        credential,
        subscribed,
      });
      this.idle();
      this.note("");
      this.paint();
    } catch (error) {
      this.fail(error);
    }
  }
}

const widgets: SubscribeWidget[] = [];
let initialized = false;

/** Shared across every box on the page, because it identifies the reader
 *  rather than a widget. */
let credential = "";

/**
 * GIS warns when initialize() is called twice and only honours the last call,
 * so one page gets one call and therefore one callback, which cannot tell which
 * button was pressed. Being on the list is a fact about the reader rather than
 * the box they used, so every box shows the same answer.
 *
 * No prompt() here, unlike the moderation page: One Tap on someone else's blog
 * is a sign-in card nobody asked for.
 */
function initializeOnce(google: GoogleIdApi, clientId: string): void {
  if (initialized) return;
  initialized = true;
  google.initialize({
    client_id: clientId,
    callback: (response) => void signedIn(response.credential),
    auto_select: false,
  });
}

/** Reads, and only reads: the buttons in the panel are what write. */
async function signedIn(token: string): Promise<void> {
  credential = token;
  for (const widget of widgets) widget.checking();
  try {
    const subscription = await apiPost<Subscription>("/subscribe/status", {
      credential,
    });
    for (const widget of widgets) widget.show(subscription);
  } catch (error) {
    for (const widget of widgets) widget.fail(error);
  }
}

async function mountAll(): Promise<void> {
  const targets = document.querySelectorAll<HTMLElement>("[data-subscribe]");
  if (targets.length === 0) return;

  // No stylesheet is injected: seopyo.css is a file the page links.
  const config = await getConfig();

  for (const target of targets) {
    const text = resolveStrings(
      target.dataset.lang,
      document.documentElement.lang,
    );
    const widget = new SubscribeWidget(target, config, text);
    widgets.push(widget);
    widget.init();
  }

  if (!config.google_client_id) return;
  for (const widget of widgets) {
    try {
      await widget.mountGoogleButton(config.google_client_id);
    } catch {
      // Subscribing by email still works, so a Google that won't load costs a
      // route rather than the feature.
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void mountAll());
} else {
  void mountAll();
}
